import moment from 'moment';
import type { NamePath } from 'antd/lib/form/interface';

import {
	DECIMAL_COMMA,
	DECIMAL_DOT,
	DEFAULT_TASK_TIME_FORMAT,
	EMPTY_STEP_FIELD,
	EMPTY_TASK_RPN
} from '@/components/views/BeraJob/BeraReportV2/constants';

import type { BeraReportV2, BeraReportV2Details, BeraReportV2StepKeyResult } from '@/types/Bera';

import type {
	EvaluationFormValues,
	EvaluationStep,
	InformationsValues,
	ReportFormValues,
	StepFormValues,
	StepKeyFormValues,
	StepsFormValues,
	UpdateBeraReportV2Payload
} from './types';

type StepKeyResultsByStep = Record<string, BeraReportV2StepKeyResult[]>;

const STEP_KEY_REQUIRED_FIELDS = ['stress_level_id', 'frequency_id', 'total_task_duration_id'] as const;

interface GetCheckedStepKeysParams {
	step: EvaluationStep;
	values?: StepFormValues;
}

interface BuildCheckedStepKeyFieldPathsParams extends GetCheckedStepKeysParams {
	fileId: string;
}

interface BuildInitialValuesParams {
	fileId: string;
	report: BeraReportV2;
	details?: BeraReportV2Details;
}

interface ToUpdatePayloadParams {
	organizationId: string;
	companyId: string;
	reportId: string;
	values: InformationsValues;
}

function toCollectionDate(value?: string | null): moment.Moment | undefined {
	if (!value) {
		return undefined;
	}

	const result = moment(value);

	return result;
}

function toTaskTime(value?: number | string): number | undefined {
	if (value === undefined || value === null || value === '') {
		return undefined;
	}

	const normalized = String(value).replace(DECIMAL_COMMA, DECIMAL_DOT);
	const result = Number(normalized);

	return result;
}

function toStepKeyFormValues(stepKeyResult: BeraReportV2StepKeyResult): StepKeyFormValues {
	const result: StepKeyFormValues = {
		step_key: !!stepKeyResult.stress_level_id,
		stress_level_id: stepKeyResult.stress_level_id,
		frequency_id: stepKeyResult.frequency_id,
		total_task_duration_id: stepKeyResult.total_task_duration_id,
		job_element: stepKeyResult.job_element ?? undefined,
		task_rpn: stepKeyResult.task_rpn ?? EMPTY_TASK_RPN
	};

	return result;
}

function groupByStep(stepKeyResults: BeraReportV2StepKeyResult[]): StepKeyResultsByStep {
	const result = stepKeyResults.reduce<StepKeyResultsByStep>((groups, stepKeyResult) => {
		const current = groups[stepKeyResult.step_id] ?? [];

		return { ...groups, [stepKeyResult.step_id]: [...current, stepKeyResult] };
	}, {});

	return result;
}

function toStepFormValues(stepKeyResults: BeraReportV2StepKeyResult[]): StepFormValues {
	const stepKeyEntries = stepKeyResults.map((stepKeyResult) => [
		stepKeyResult.step_key_id,
		toStepKeyFormValues(stepKeyResult)
	]);
	const stepKeys: StepFormValues = Object.fromEntries(stepKeyEntries);
	const isEmptyStep = stepKeyResults.every((stepKeyResult) => !stepKeyResult.stress_level_id);

	const result: StepFormValues = { ...stepKeys, [EMPTY_STEP_FIELD]: isEmptyStep };

	return result;
}

export function buildStepsInitialValues(stepKeyResults: BeraReportV2StepKeyResult[]): StepsFormValues {
	const byStep = groupByStep(stepKeyResults);
	const stepEntries = Object.entries(byStep).map(([stepId, results]) => [stepId, toStepFormValues(results)]);

	const result: StepsFormValues = Object.fromEntries(stepEntries);

	return result;
}

export function buildInitialValues({ fileId, report, details }: BuildInitialValuesParams): EvaluationFormValues {
	const informations: InformationsValues = {
		task_id: details?.task_id ?? report.task_id,
		task_time: details?.task_time ?? undefined,
		task_time_format: details?.task_time_format ?? DEFAULT_TASK_TIME_FORMAT,
		has_known_injury: details?.has_known_injury ?? undefined,
		collection_date: toCollectionDate(details?.collection_date),
		has_no_risk: details?.has_no_risk ?? false
	};

	const steps = buildStepsInitialValues(details?.step_key_results ?? []);

	const reportValues: ReportFormValues = Object.assign({}, informations, steps);

	const result = { [fileId]: reportValues };

	return result;
}

export function toUpdatePayload({
	organizationId,
	companyId,
	reportId,
	values
}: ToUpdatePayloadParams): UpdateBeraReportV2Payload {
	const result: UpdateBeraReportV2Payload = {
		organization_id: organizationId,
		company_id: companyId,
		id: reportId,
		task_id: values.task_id,
		task_time: toTaskTime(values.task_time),
		task_time_format: values.task_time_format,
		has_known_injury: values.has_known_injury,
		collection_date: values.collection_date?.toISOString(),
		has_no_risk: !!values.has_no_risk
	};

	return result;
}

function isCheckedStepKey(values: StepFormValues | undefined, stepKeyId: string): boolean {
	const stepKeyValues = values?.[stepKeyId];

	if (typeof stepKeyValues !== 'object') {
		return false;
	}

	const result = !!stepKeyValues?.step_key;

	return result;
}

export function getCheckedStepKeys({ step, values }: GetCheckedStepKeysParams): EvaluationStep['step_key'] {
	const stepKeys = step.step_key ?? [];

	const result = stepKeys.filter((stepKey) => isCheckedStepKey(values, stepKey.id));

	return result;
}

export function buildCheckedStepKeyFieldPaths({
	fileId,
	step,
	values
}: BuildCheckedStepKeyFieldPathsParams): NamePath[] {
	const checkedStepKeys = getCheckedStepKeys({ step, values });

	const result = checkedStepKeys.flatMap((stepKey) =>
		STEP_KEY_REQUIRED_FIELDS.map((field) => [fileId, step.id, stepKey.id, field])
	);

	return result;
}
